import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { CATEGORY_ORDER, buildTree, buildIndex, TreeNodeItem, TextbookExplorer, subjectDisplayLabel } from '../components/CurriculumTree';

const CURRENT_YEAR = new Date().getFullYear();

function groupExistingByCat(existing) {
  const groups = {};
  existing.forEach((a) => {
    const key = a.cat || '기타';
    if (!groups[key]) groups[key] = [];
    groups[key].push(a);
  });
  const orderedKeys = [
    ...CATEGORY_ORDER.filter((c) => groups[c]),
    ...Object.keys(groups).filter((k) => !CATEGORY_ORDER.includes(k)),
  ];
  return orderedKeys.map((cat) => ({ cat, items: groups[cat] }));
}

const TYPECLS = '정규';

export default function AnnualAssignment() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set(['root']));
  const [tbExpanded, setTbExpanded] = useState(new Set()); // 표준교재 패널 자체의 펼침 상태 (좌측 트리와 별개로 단독 동작 가능)
  const [selectedId, setSelectedId] = useState('root');
  const [expandedParts, setExpandedParts] = useState(new Set());
  const [sectionsCache, setSectionsCache] = useState({}); // crsmatno -> sections[]

  const [teachers, setTeachers] = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [locationFilter, setLocationFilter] = useState('internal');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [checkedRows, setCheckedRows] = useState(new Set()); // "part:crsmatno" | "sect:crssectno"
  const [baskets, setBaskets] = useState({}); // teachid -> [{crsmatno, label}]
  const [hourInputs, setHourInputs] = useState({}); // teachid -> { crsmatno -> {thrytime, practime} }
  const [rowStatus, setRowStatus] = useState({}); // teachid -> {saving, message}

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/curriculum-tree', { params: { year } })
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    api.get('/admin/teachers', { params: { location: locationFilter } }).then((res) => setTeachers(res.data));
  }, [locationFilter]);

  const loadExistingAssignments = React.useCallback(() => {
    api.get('/admin/assignments', { params: { year, typecls: TYPECLS } }).then((res) => setExistingAssignments(res.data));
  }, [year]);

  useEffect(() => {
    loadExistingAssignments();
  }, [loadExistingAssignments]);

  const specialties = useMemo(
    () => Array.from(new Set(teachers.map((t) => t.specialty).filter(Boolean))).sort(),
    [teachers]
  );
  // 이름 콤보박스 옵션: 담당이 선택돼 있으면 그 담당 소속 교사 이름만 나오도록 좁힌다
  const namesForFilter = useMemo(
    () =>
      Array.from(
        new Set(
          teachers
            .filter((t) => !specialtyFilter || t.specialty === specialtyFilter)
            .map((t) => t.name)
            .filter(Boolean)
        )
      ).sort(),
    [teachers, specialtyFilter]
  );
  const handleSpecialtyFilterChange = (value) => {
    setSpecialtyFilter(value);
    setNameFilter(''); // 담당이 바뀌면 더는 목록에 없을 수 있는 이름 선택을 초기화
  };
  const filteredTeachers = useMemo(
    () =>
      teachers
        .filter((t) => !specialtyFilter || t.specialty === specialtyFilter)
        .filter((t) => !nameFilter || t.name === nameFilter),
    [teachers, specialtyFilter, nameFilter]
  );

  const tree = useMemo(() => buildTree(rows), [rows]);
  const index = useMemo(() => {
    const idx = {};
    buildIndex(tree, null, idx);
    return idx;
  }, [tree]);

  useEffect(() => {
    if (!index[selectedId]) setSelectedId('root');
  }, [index, selectedId]);

  const toggleNode = (id) => {
    const entry = index[id];
    if (entry && entry.node.children && entry.node.children.length > 0) {
      // 카테고리: 지정교과목 트리에서 펼치고/접는 동작을 표준교재 패널에 그대로 반영한다.
      // 표준교재를 단독으로 탐색하다 남아있던 다른 카테고리는 여기서 덮어써서 정리한다
      // (안 그러면 지정교과목 클릭과 무관한 카테고리가 계속 같이 펼쳐진 채로 남아 지저분해짐).
      const willOpen = !expanded.has(id);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      setTbExpanded(willOpen ? new Set([id]) : new Set());
    } else if (entry) {
      // 챕터(리프): 표준교재에는 별도 챕터 단계가 없으므로, 상위 과목만 펼쳐서
      // 그 안에 있는 표준교재 PART들을 보여준다 (다른 과목은 정리)
      setTbExpanded(entry.parent ? new Set([entry.parent.id]) : new Set());
    }
    setSelectedId(id);
  };

  const toggleTbNode = (id) => {
    setTbExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePart = (keyId, crsmatno) => {
    const key = `${keyId}::${crsmatno}`;
    setExpandedParts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        if (!sectionsCache[crsmatno]) {
          api.get(`/materials/${crsmatno}/sections`).then((res) => {
            setSectionsCache((sc) => ({ ...sc, [crsmatno]: res.data }));
          });
        }
      }
      return next;
    });
  };

  const toggleCheckRow = (rowKey) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleDropOnBasket = (teachid, payload) => {
    const { crsmatno, label, crssectno, sectionLabel } = payload;
    setBaskets((prev) => {
      const list = prev[teachid] || [];
      const idx = list.findIndex((item) => item.crsmatno === crsmatno);
      if (idx === -1) {
        return {
          ...prev,
          [teachid]: [
            ...list,
            { crsmatno, label, sections: crssectno ? [{ crssectno, label: sectionLabel }] : [] },
          ],
        };
      }
      if (!crssectno) return prev; // 챕터 전체가 이미 담겨 있음
      const existing = list[idx];
      if (existing.sections.some((s) => s.crssectno === crssectno)) return prev;
      const nextList = [...list];
      nextList[idx] = { ...existing, sections: [...existing.sections, { crssectno, label: sectionLabel }] };
      return { ...prev, [teachid]: nextList };
    });
  };

  const removeFromBasket = (teachid, crsmatno) => {
    setBaskets((prev) => ({
      ...prev,
      [teachid]: (prev[teachid] || []).filter((item) => item.crsmatno !== crsmatno),
    }));
  };

  const removeSectionFromBasket = (teachid, crsmatno, crssectno) => {
    setBaskets((prev) => ({
      ...prev,
      [teachid]: (prev[teachid] || []).map((item) =>
        item.crsmatno === crsmatno
          ? { ...item, sections: item.sections.filter((s) => s.crssectno !== crssectno) }
          : item
      ),
    }));
  };

  const handleDeleteExistingAssignment = async (asnid) => {
    await api.delete(`/admin/assignments/${asnid}`);
    loadExistingAssignments();
  };

  const setHourInput = (teachid, crsmatno, field, value) => {
    setHourInputs((prev) => ({
      ...prev,
      [teachid]: {
        ...prev[teachid],
        [crsmatno]: { ...prev[teachid]?.[crsmatno], [field]: value },
      },
    }));
  };

  const handleSaveAssignment = async (teachid) => {
    const items = baskets[teachid] || [];
    if (items.length === 0) return;
    const hoursByChapter = hourInputs[teachid] || {};
    setRowStatus((prev) => ({ ...prev, [teachid]: { saving: true, message: '' } }));
    try {
      for (const item of items) {
        const hours = hoursByChapter[item.crsmatno] || {};
        await api.post('/admin/assignments', {
          asnyear: year,
          teachid,
          crsmatno: item.crsmatno,
          typecls: TYPECLS,
          thrytime: Number(hours.thrytime) || 0,
          practime: Number(hours.practime) || 0,
          section_codes: item.sections.map((s) => s.crssectno),
        });
      }
      setBaskets((prev) => ({ ...prev, [teachid]: [] }));
      setHourInputs((prev) => ({ ...prev, [teachid]: {} }));
      setCheckedRows((prev) => {
        const next = new Set(prev);
        items.forEach((item) => {
          next.delete(`part:${item.crsmatno}`);
          item.sections.forEach((s) => next.delete(`sect:${s.crssectno}`));
        });
        return next;
      });
      setRowStatus((prev) => ({ ...prev, [teachid]: { saving: false, message: '배정 저장 완료' } }));
      loadExistingAssignments();
    } catch (err) {
      setRowStatus((prev) => ({
        ...prev,
        [teachid]: { saving: false, message: err.response?.data?.detail || '저장 실패' },
      }));
    }
  };

  const selected = index[selectedId]?.node || tree;

  // 지정교과목에서 챕터가 선택된 경우, 그 챕터에 속한 표준교재 PART들의 crsmatno 집합.
  // 표준교재 패널은 이 집합에 속한 PART에만 체크박스(배정)를 활성화한다.
  const activeCrsmatnos = useMemo(() => {
    if (selected.type !== 'chapter') return null;
    return new Set(selected.relatedParts.map((p) => p.crsmatno));
  }, [selected]);

  return (
    <div className="page">
      <div className="page-title-row">
        <label>연도</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <h2>항공정비사 지정교과목 배정관리</h2>
      </div>

      <div className="card">
        {loading && <div className="form-message">불러오는 중...</div>}
        <div className="curr-layout">
          <div className="curr-left-col">
            <div className="curr-tree-label">항공안전법 지정교과목</div>
            <nav className="curr-tree">
              <ul className="curr-tree-list curr-tree-root">
                {tree.children.map((node) => (
                  <TreeNodeItem
                    key={node.id}
                    node={node}
                    expanded={expanded}
                    selectedId={selectedId}
                    onToggle={toggleNode}
                  />
                ))}
              </ul>
            </nav>

            <div className="curr-textbook-panel">
              <div className="curr-textbook-panel-title">
                <span className="curr-node-icon">📘</span> 표준교재
              </div>
              <TextbookExplorer
                categories={tree.children}
                tbExpanded={tbExpanded}
                onToggleTb={toggleTbNode}
                expandedParts={expandedParts}
                onTogglePart={togglePart}
                sectionsCache={sectionsCache}
                checkedRows={checkedRows}
                onToggleCheckRow={toggleCheckRow}
                activeCrsmatnos={activeCrsmatnos}
              />
            </div>
          </div>

          <div className="curr-main">
            <MainContent
              node={selected}
              teachers={filteredTeachers}
              specialties={specialties}
              locationFilter={locationFilter}
              onLocationFilterChange={setLocationFilter}
              specialtyFilter={specialtyFilter}
              onSpecialtyFilterChange={handleSpecialtyFilterChange}
              namesForFilter={namesForFilter}
              nameFilter={nameFilter}
              onNameFilterChange={setNameFilter}
              existingAssignments={existingAssignments}
              baskets={baskets}
              hourInputs={hourInputs}
              rowStatus={rowStatus}
              onDropOnBasket={handleDropOnBasket}
              onRemoveFromBasket={removeFromBasket}
              onRemoveSectionFromBasket={removeSectionFromBasket}
              onHourInput={setHourInput}
              onSaveAssignment={handleSaveAssignment}
              onDeleteExistingAssignment={handleDeleteExistingAssignment}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MainContent({
  node,
  teachers,
  specialties,
  locationFilter,
  onLocationFilterChange,
  specialtyFilter,
  onSpecialtyFilterChange,
  namesForFilter,
  nameFilter,
  onNameFilterChange,
  existingAssignments,
  baskets,
  hourInputs,
  rowStatus,
  onDropOnBasket,
  onRemoveFromBasket,
  onRemoveSectionFromBasket,
  onHourInput,
  onSaveAssignment,
  onDeleteExistingAssignment,
}) {
  // 교사별 "이미 배정됨" 목록 - 챕터로 필터링하지 않고 해당 연도 전체 배정을 보여준다
  const existingByTeacher = {};
  existingAssignments.forEach((a) => {
    if (!existingByTeacher[a.teachid]) existingByTeacher[a.teachid] = [];
    existingByTeacher[a.teachid].push(a);
  });

  // 마우스 끌기(배정하기)는 지정교과목에서 챕터가 선택된 경우에만 활성화된다
  const isChapter = node.type === 'chapter';

  return (
    <>
      <div className="curr-chapter-header">
        <div className="curr-chapter-header-right">
          <h4 className="curr-section-heading">교사 과목 배정</h4>
          <div className="curr-teacher-filter">
            <label>구분</label>
            <select value={locationFilter} onChange={(e) => onLocationFilterChange(e.target.value)}>
              <option value="internal">원내교사</option>
              <option value="external">원외교사</option>
            </select>
          </div>
          <div className="curr-teacher-filter">
            <label>담당</label>
            <select value={specialtyFilter} onChange={(e) => onSpecialtyFilterChange(e.target.value)}>
              <option value="">전체</option>
              {specialties.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="curr-teacher-filter">
            <label>이름</label>
            <select value={nameFilter} onChange={(e) => onNameFilterChange(e.target.value)}>
              <option value="">전체</option>
              {namesForFilter.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {isChapter && (
            <div className="curr-header-chips">
              <span className="curr-chip curr-chip-theory">학과 <span className="curr-num">{node.theory}</span> h</span>
              <span className="curr-chip curr-chip-practice">실습 <span className="curr-num">{node.practice}</span> h</span>
            </div>
          )}
        </div>
      </div>

      <div className="curr-assign-main">
        <TeacherAssignTable
          teachers={teachers}
          existingByTeacher={existingByTeacher}
          baskets={baskets}
          hourInputs={hourInputs}
          rowStatus={rowStatus}
          onDropOnBasket={onDropOnBasket}
          onRemoveFromBasket={onRemoveFromBasket}
          onRemoveSectionFromBasket={onRemoveSectionFromBasket}
          onHourInput={onHourInput}
          onSaveAssignment={onSaveAssignment}
          onDeleteExistingAssignment={onDeleteExistingAssignment}
          assignable
        />
      </div>
    </>
  );
}

function TeacherAssignTable({
  teachers,
  existingByTeacher,
  baskets = {},
  hourInputs = {},
  rowStatus = {},
  onDropOnBasket,
  onRemoveFromBasket,
  onRemoveSectionFromBasket,
  onHourInput,
  onSaveAssignment,
  onDeleteExistingAssignment,
  assignable = true,
}) {
  return (
    <div className="table-scroll">
      <table className="data-table curr-assign-table">
        <thead>
          <tr>
            <th>학년</th>
            <th>담당</th>
            <th>이름</th>
            <th>배정시간</th>
            <th>과목명</th>
            <th>배정과목</th>
            {assignable && <th>배정하기 (챕터별 학과·실습 시간)</th>}
            {assignable && <th></th>}
          </tr>
        </thead>
        <tbody>
          {teachers.map((t) => {
            const basket = baskets[t.teachid] || [];
            const hoursByChapter = hourInputs[t.teachid] || {};
            const status = rowStatus[t.teachid] || {};
            const existing = existingByTeacher[t.teachid] || [];
            const totalThry = existing.reduce((sum, a) => sum + (a.thrytime || 0), 0);
            const totalPrac = existing.reduce((sum, a) => sum + (a.practime || 0), 0);
            const existingGroups = groupExistingByCat(existing);
            return (
              <tr key={t.teachid}>
                <td>{t.grade ?? ''}</td>
                <td>{t.specialty ?? ''}</td>
                <td>{t.name}</td>
                <td className="curr-total-cell" title="총 배정 학과/실습 시간">
                  <span className="curr-teacher-total">{String(totalThry).padStart(2, '0')}/{String(totalPrac).padStart(2, '0')}</span>
                </td>
                <td className="curr-subject-cell">
                  {existingGroups.length === 0 ? (
                    <span className="curr-basket-placeholder">-</span>
                  ) : (
                    <div className="curr-basket-items">
                      {existingGroups.map(({ cat }) => (
                        <div key={cat} className="curr-existing-group">
                          <div className="curr-existing-group-label">{subjectDisplayLabel(cat)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="curr-existing-cell">
                  {existingGroups.length === 0 ? (
                    <span className="curr-basket-placeholder">-</span>
                  ) : (
                    <div className="curr-basket-items">
                      {existingGroups.map(({ cat, items }) => (
                        <div key={cat} className="curr-existing-group">
                          {items.map((a) => (
                            <div key={a.asnid} className="curr-existing-entry">
                              <span
                                className="curr-existing-chip"
                                title="클릭하면 이 배정이 삭제됩니다"
                                onClick={() => {
                                  if (window.confirm(`"${a.chapter}" 배정을 삭제하시겠습니까?`)) {
                                    onDeleteExistingAssignment(a.asnid);
                                  }
                                }}
                              >
                                {a.chapter} ({a.thrytime}/{a.practime}) ✕
                              </span>
                              {a.sections.length > 0 && (
                                <div className="curr-basket-sections">
                                  {a.sections.map((s) => (
                                    <span key={s.crssectno} className="curr-existing-section-tag">
                                      {s.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                {assignable && (
                  <td
                    className={`curr-basket-cell${basket.length === 0 ? ' curr-basket-empty' : ''}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      let items;
                      try {
                        items = JSON.parse(e.dataTransfer.getData('application/json') || '[]');
                      } catch {
                        return;
                      }
                      if (!Array.isArray(items)) items = [items];
                      items.filter((item) => item && item.crsmatno).forEach((item) => onDropOnBasket(t.teachid, item));
                    }}
                  >
                    {basket.length === 0 ? (
                      <span className="curr-basket-placeholder">여기로 끌어놓기</span>
                    ) : (
                      <div className="curr-basket-items">
                        {basket.map((item) => {
                          const hours = hoursByChapter[item.crsmatno] || {};
                          return (
                            <div key={item.crsmatno} className="curr-basket-entry">
                              <div className="curr-basket-entry-row">
                                <span
                                  className="curr-basket-chip"
                                  title="클릭하면 챕터 전체가 제거됩니다"
                                  onClick={() => onRemoveFromBasket(t.teachid, item.crsmatno)}
                                >
                                  {item.label} ✕
                                </span>
                                <span className="curr-basket-hour-inputs">
                                  <input
                                    type="number"
                                    min="0"
                                    className="curr-hour-input-sm"
                                    placeholder="학과"
                                    title="학과 시간"
                                    value={hours.thrytime ?? ''}
                                    onChange={(e) => onHourInput(t.teachid, item.crsmatno, 'thrytime', e.target.value)}
                                  />
                                  <input
                                    type="number"
                                    min="0"
                                    className="curr-hour-input-sm"
                                    placeholder="실습"
                                    title="실습 시간"
                                    value={hours.practime ?? ''}
                                    onChange={(e) => onHourInput(t.teachid, item.crsmatno, 'practime', e.target.value)}
                                  />
                                </span>
                              </div>
                              {item.sections.length > 0 && (
                                <div className="curr-basket-sections">
                                  {item.sections.map((s) => (
                                    <span
                                      key={s.crssectno}
                                      className="curr-basket-section-tag"
                                      title="클릭하면 이 섹션만 제거됩니다"
                                      onClick={() => onRemoveSectionFromBasket(t.teachid, item.crsmatno, s.crssectno)}
                                    >
                                      {s.label} ✕
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                )}
                {assignable && (
                  <td>
                    <button
                      type="button"
                      className="curr-save-btn"
                      disabled={basket.length === 0 || status.saving}
                      onClick={() => onSaveAssignment(t.teachid)}
                    >
                      저장
                    </button>
                    {status.message && <div className="curr-row-message">{status.message}</div>}
                  </td>
                )}
              </tr>
            );
          })}
          {teachers.length === 0 && (
            <tr>
              <td colSpan={assignable ? 8 : 6} className="empty-row">등록된 교사가 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
